import classNames from "classnames";
import { MouseEvent, useMemo, useRef, useState } from "react";
import { EnrichmentModelOption, EnrichmentModelSelectedStatus } from "types";

import TooltipWrapper from "views/uikit/TooltipWrapper";
import { getVendorLogo } from "helpers/utility/utilities";
import ModelTypeBadge from "views/components/ModelTypeBadge";
import { displayForamttedDate } from "helpers/utility/formatters";

import XIcon from "assets/icons/XIcon";
import ArrowIcon from "assets/icons/ArrowIcon";
import ExploreIcon from "assets/icons/ExploreIcon";
import CalenderIcon from "assets/icons/CalenderIcon";

import styles from "./style.module.scss";

const SingleModel = ({ model, onSelect, isSelected }: SingleModelProps) => {
  const [tooltipContent, setTooltipContent] = useState<string | null>(null);
  const modelDescriptionRef = useRef<HTMLDivElement>(null);
  const isActive = model.status === "Active";
  const assetPath = getVendorLogo(model.vendor_logo_uri);
  const vendorLogoFileName = model.vendor_logo_uri?.split(".")?.[0] || "";

  const alreadySelected =
    model.selectedStatus !== EnrichmentModelSelectedStatus.MODEL_NOT_SELECTED;

  const isEnriched =
    model.selectedStatus ===
    EnrichmentModelSelectedStatus.MODEL_PREVIOUSLY_SELECTED;

  const containerClassnames = classNames(styles.modelContainer, {
    [styles.selectedContainer]: isSelected,
    [styles.enrichedModelContainer]: alreadySelected,
    [styles.comingSoonContainer]: !isActive,
    [styles.defaultPointer]: alreadySelected || !isActive,
  });

  const headerleftClassnames = classNames(styles.headerLeft, {
    [styles.inactiveHeaderLeft]: !isActive,
    [styles.enrichedHeaderLeft]: isEnriched,
  });

  const descriptionClassnames = classNames(styles.modelDescription, {
    [styles.whiteFont]: isSelected || alreadySelected,
  });

  const handleLinkClick = (event: MouseEvent) => {
    event.stopPropagation();
    window.open(
      model.documentation_uri ||
        `https://www.google.com/search?q=${model.name}`,
      "_blank"
    );
  };

  const renderSelectionButton = (model: EnrichmentModelOption) => {
    if (!isActive) {
      return <div className={styles.comingSoon}>⚡️ Coming Soon</div>;
    } else if (isEnriched) {
      return <div className={styles.comingSoon}>Enriched</div>;
    } else {
      const selectionButtonClassnames = classNames(styles.selectionButton, {
        [styles.disabledButton]: alreadySelected,
      });

      const iconClassnames = classNames({
        [styles.selectedCircle]: isSelected || alreadySelected,
        [styles.unselectedCircle]: !isSelected && !alreadySelected,
        [styles.unselectableCircle]: model.selectable.value === false,
      });

      if (model.selectable.reason.length > 0) {
        return (
          <div
            className={selectionButtonClassnames}
            data-tooltip-id={"preview-model-selection-button-tooltip"}
            data-tooltip-place={"top-end"}
            data-tooltip-content={model.selectable.reason}
            data-testid="model-options-selection-button-unselectable"
          >
            <div className={iconClassnames}></div>
            <TooltipWrapper
              id="preview-model-selection-button-tooltip"
              customClass="modelTooltip"
            />
          </div>
        );
      } else {
        return (
          <div
            className={selectionButtonClassnames}
            data-testid="model-options-selection-button-selectable"
          >
            <div className={iconClassnames}></div>
          </div>
        );
      }
    }
  };

  const handleMouseOver = () => {
    if (modelDescriptionRef.current) {
      const shouldShowTooltip =
        modelDescriptionRef.current.scrollHeight >
        modelDescriptionRef.current.clientHeight;

      if (shouldShowTooltip) {
        setTooltipContent(model.description);
      }
    }
  };

  const handleMouseLeave = () => {
    setTooltipContent(null);
  };

  return (
    <div
      className={styles.modelWrapper}
      onClick={() => !alreadySelected && onSelect(model, isActive)}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <div className={containerClassnames}>
        <div className={styles.modelHeader}>
          <div className={headerleftClassnames}>
            <div className={styles.icon}>
              <img src={assetPath} alt={vendorLogoFileName} width={"24px"} />
            </div>
            <div className={styles.title} title={model.name}>
              {model.name}
            </div>
          </div>
          <div className={styles.headerRight}>
            {renderSelectionButton(model)}
          </div>
        </div>
        <ModelTypeBadge modelType={model.type} />
        <div
          className={descriptionClassnames}
          data-tooltip-id={"preview-model-description-tooltip"}
          data-tooltip-place={"top-end"}
          data-tooltip-content={model.description}
          ref={modelDescriptionRef}
        >
          {model.description}
        </div>
        {tooltipContent && (
          <TooltipWrapper
            id="preview-model-description-tooltip"
            customClass="modelTooltip"
            content={tooltipContent}
          />
        )}
        <div className={styles.modelDivider}></div>
        <div className={styles.modelListFooter}>
          <div className={styles.releaserDate}>
            {model.release_date && (
              <>
                <div className={styles.calenderIcon}>
                  <CalenderIcon />
                </div>
                <div className={styles.releaserDateText}>
                  Released {displayForamttedDate(model.release_date)}
                </div>
              </>
            )}
          </div>
          <div
            data-testid="model-options-single-model-link-btn"
            className={styles.linkButton}
            onClick={handleLinkClick}
          >
            <ExploreIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

const ModelOptionsModal = ({
  modelOptions,
  selectedModel,
  onCreatePreviews,
  onCloseModelOptions,
  onOptionSelection,
}: ModelOptionsModalProps) => {
  const hasOnlyEnrichedModels = useMemo(() => {
    return modelOptions.every(
      (model) =>
        model.selectedStatus ===
          EnrichmentModelSelectedStatus.MODEL_PREVIOUSLY_SELECTED ||
        model.status === "Coming Soon"
    );
  }, [modelOptions]);

  const generateButtonClassnames = classNames(styles.generatePreviewsButton, {
    [styles.disabledButton]: !selectedModel,
  });

  return (
    <div className={styles.overlay}>
      <div className={styles.modelDialogWrapper}>
        <div className={styles.modelDialogHeader}>
          <div className={styles.modelTitle}>
            Choose a Model to Enrich Your Data
          </div>
          <div
            onClick={onCloseModelOptions}
            className={styles.closeButton}
            data-testid="enrichment-page-close-model-options-btn"
          >
            <XIcon />
          </div>
        </div>
        <div className={styles.divider}></div>
        <div className={styles.modelBody}>
          {modelOptions.map((model, index) => {
            const isSelected = selectedModel?.id === model.id;
            return (
              <SingleModel
                model={model}
                key={model.id + index}
                isSelected={isSelected}
                onSelect={onOptionSelection}
              />
            );
          })}
        </div>
        <div className={styles.divider}></div>
        <div className={styles.modelFooter}>
          <div
            className={styles.generatePreviewButtonWrapper}
            data-tooltip-id="all-models-selected-generate-button-tooltip"
            data-tooltip-content="All available models have been enriched, the new models will be available soon"
            data-tooltip-offset={2}
          >
            <div
              className={generateButtonClassnames}
              onClick={() => {
                if (selectedModel) {
                  onCreatePreviews(selectedModel);
                }
              }}
            >
              Generate Models Preview <ArrowIcon variant="thin" />
            </div>
            {hasOnlyEnrichedModels && (
              <TooltipWrapper
                id="all-models-selected-generate-button-tooltip"
                customClass="modelTooltip"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelOptionsModal;

interface ModelOptionsModalProps {
  onCloseModelOptions: () => void;
  modelOptions: EnrichmentModelOption[];
  selectedModel: EnrichmentModelOption | null;
  onCreatePreviews: (model: EnrichmentModelOption) => void;
  onOptionSelection: (model: EnrichmentModelOption, isActive: boolean) => void;
}

interface SingleModelProps {
  isSelected: boolean;
  model: EnrichmentModelOption;
  onSelect: (model: EnrichmentModelOption, isModelActive: boolean) => void;
}
